/**
 * PropertyContactForm - Formulario de contacto para propiedades
 *
 * Permite a usuarios enviar consultas sobre propiedades específicas.
 * Guarda los leads en Firebase y notifica a los asesores.
 */

import React, { useState } from 'react'
import { collection, addDoc, Timestamp } from 'firebase/firestore'
import { db } from '../firebaseConfig'
import { useUser } from '../context/UserContext'
import Swal from 'sweetalert2'

interface PropertyContactFormProps {
  propertyId: string
  propertyName: string
  asesorIds?: string[]
  inmobiliariaId?: string
  variant?: 'card' | 'inline' | 'modal'
  onSuccess?: () => void
}

interface FormData {
  nombre: string
  email: string
  telefono: string
  mensaje: string
  tipoContacto: 'informacion' | 'visita' | 'oferta' | 'otro'
}

const tiposContacto = [
  { value: 'informacion', label: 'Solicitar información', icon: 'fa-info-circle' },
  { value: 'visita', label: 'Agendar visita', icon: 'fa-calendar-check' },
  { value: 'oferta', label: 'Hacer una oferta', icon: 'fa-hand-holding-dollar' },
  { value: 'otro', label: 'Otro asunto', icon: 'fa-question-circle' }
]

export default function PropertyContactForm({
  propertyId,
  propertyName,
  asesorIds = [],
  inmobiliariaId,
  variant = 'card',
  onSuccess
}: PropertyContactFormProps) {
  const { user } = useUser()

  const [formData, setFormData] = useState<FormData>({
    nombre: user?.nombre || '',
    email: user?.correo || '',
    telefono: user?.telefono || '',
    mensaje: '',
    tipoContacto: 'informacion'
  })

  const [loading, setLoading] = useState<boolean>(false)
  const [errors, setErrors] = useState<Partial<FormData>>({})

  // Validación
  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {}

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido'
    }

    if (!formData.telefono.trim()) {
      newErrors.telefono = 'El teléfono es requerido'
    } else if (!/^[\d\s\-\+\(\)]{10,}$/.test(formData.telefono)) {
      newErrors.telefono = 'Teléfono inválido (mínimo 10 dígitos)'
    }

    if (!formData.mensaje.trim()) {
      newErrors.mensaje = 'El mensaje es requerido'
    } else if (formData.mensaje.trim().length < 10) {
      newErrors.mensaje = 'El mensaje debe tener al menos 10 caracteres'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Manejar cambios
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    // Limpiar error del campo modificado
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
  }

  // Enviar formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setLoading(true)

    try {
      // Guardar lead en Firebase
      await addDoc(collection(db, 'leads'), {
        // Datos del lead
        nombre: formData.nombre.trim(),
        email: formData.email.trim().toLowerCase(),
        telefono: formData.telefono.trim(),
        mensaje: formData.mensaje.trim(),
        tipoContacto: formData.tipoContacto,

        // Datos de la propiedad
        propertyId,
        propertyName,

        // Datos de asignación
        asesorIds,
        inmobiliariaId: inmobiliariaId || null,

        // Metadata
        userId: user?.id || null,
        estado: 'nuevo',
        fechaCreacion: Timestamp.now(),
        origen: 'web',
        leido: false
      })

      // Mostrar confirmación
      Swal.fire({
        icon: 'success',
        title: 'Mensaje enviado',
        html: `
          <p>Tu consulta sobre <strong>${propertyName}</strong> ha sido enviada correctamente.</p>
          <p class="text-muted small">Un asesor se pondrá en contacto contigo pronto.</p>
        `,
        confirmButtonText: 'Entendido'
      })

      // Limpiar formulario
      setFormData({
        nombre: user?.nombre || '',
        email: user?.correo || '',
        telefono: user?.telefono || '',
        mensaje: '',
        tipoContacto: 'informacion'
      })

      onSuccess?.()
    } catch (err) {
      console.error('Error enviando formulario de contacto:', err)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo enviar el mensaje. Intenta de nuevo.'
      })
    } finally {
      setLoading(false)
    }
  }

  // Renderizar según variante
  const formContent = (
    <form onSubmit={handleSubmit} noValidate>
      {/* Tipo de contacto */}
      <div className="mb-3">
        <label className="form-label fw-semibold">
          <i className="fa-solid fa-comment-dots me-2 text-primary"></i>
          ¿En qué te podemos ayudar?
        </label>
        <div className="d-flex flex-wrap gap-2">
          {tiposContacto.map(tipo => (
            <button
              key={tipo.value}
              type="button"
              className={`btn btn-sm ${formData.tipoContacto === tipo.value ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setFormData(prev => ({ ...prev, tipoContacto: tipo.value as FormData['tipoContacto'] }))}
            >
              <i className={`fa-solid ${tipo.icon} me-1`}></i>
              {tipo.label}
            </button>
          ))}
        </div>
      </div>

      {/* Nombre */}
      <div className="mb-3">
        <label htmlFor="contactNombre" className="form-label">
          Nombre completo <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          id="contactNombre"
          name="nombre"
          className={`form-control ${errors.nombre ? 'is-invalid' : ''}`}
          value={formData.nombre}
          onChange={handleChange}
          placeholder="Tu nombre"
          required
        />
        {errors.nombre && <div className="invalid-feedback">{errors.nombre}</div>}
      </div>

      {/* Email y Teléfono en una fila */}
      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="contactEmail" className="form-label">
            Email <span className="text-danger">*</span>
          </label>
          <input
            type="email"
            id="contactEmail"
            name="email"
            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
            value={formData.email}
            onChange={handleChange}
            placeholder="tu@email.com"
            required
          />
          {errors.email && <div className="invalid-feedback">{errors.email}</div>}
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="contactTelefono" className="form-label">
            Teléfono <span className="text-danger">*</span>
          </label>
          <input
            type="tel"
            id="contactTelefono"
            name="telefono"
            className={`form-control ${errors.telefono ? 'is-invalid' : ''}`}
            value={formData.telefono}
            onChange={handleChange}
            placeholder="(123) 456-7890"
            required
          />
          {errors.telefono && <div className="invalid-feedback">{errors.telefono}</div>}
        </div>
      </div>

      {/* Mensaje */}
      <div className="mb-3">
        <label htmlFor="contactMensaje" className="form-label">
          Mensaje <span className="text-danger">*</span>
        </label>
        <textarea
          id="contactMensaje"
          name="mensaje"
          rows={4}
          className={`form-control ${errors.mensaje ? 'is-invalid' : ''}`}
          value={formData.mensaje}
          onChange={handleChange}
          placeholder={
            formData.tipoContacto === 'visita'
              ? '¿Cuándo te gustaría agendar la visita? ¿Tienes alguna preferencia de horario?'
              : formData.tipoContacto === 'oferta'
                ? 'Describe tu oferta y condiciones...'
                : 'Escribe tu mensaje aquí...'
          }
          required
        ></textarea>
        {errors.mensaje && <div className="invalid-feedback">{errors.mensaje}</div>}
      </div>

      {/* Botón enviar */}
      <button
        type="submit"
        className="btn btn-primary w-100"
        disabled={loading}
      >
        {loading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Enviando...
          </>
        ) : (
          <>
            <i className="fa-solid fa-paper-plane me-2"></i>
            Enviar mensaje
          </>
        )}
      </button>

      {/* Aviso de privacidad */}
      <p className="text-muted small mt-3 mb-0">
        <i className="fa-solid fa-shield-halved me-1"></i>
        Al enviar este formulario, aceptas que procesemos tus datos para responder a tu consulta.
      </p>
    </form>
  )

  // Variante card
  if (variant === 'card') {
    return (
      <div className="card shadow-sm">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">
            <i className="fa-solid fa-envelope me-2"></i>
            Contactar sobre esta propiedad
          </h5>
        </div>
        <div className="card-body">
          {formContent}
        </div>
      </div>
    )
  }

  // Variante inline
  if (variant === 'inline') {
    return (
      <div className="property-contact-form">
        <h5 className="mb-4">
          <i className="fa-solid fa-envelope me-2 text-primary"></i>
          Enviar consulta
        </h5>
        {formContent}
      </div>
    )
  }

  // Variante modal (solo contenido)
  return formContent
}
